import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { GeneralService } from '../../shared/services/general.service';

import {
  IUser,
  ILoginFormModel,
  LoginRet,
  IChangePasswordModel,
  IForgotPasswordModel,
  IResetPasswordModel,
  ISetupAccountModel,
} from '../../shared/models/identity.models';

@Injectable({
  providedIn: 'root',
})
export class IdentityService {
  private readonly baseUrl = '/api/identity';

  constructor(
    private http: HttpClient,
    private generalService: GeneralService) { }

  // Login method
  login(user: ILoginFormModel): Observable<LoginRet> {
    localStorage.removeItem('adminMode');

    return this.http.post<IUser>(`${this.baseUrl}/login`, user).pipe(
      map((response: IUser) => {
        // Handle errors in the response
        if (!response || response.loginStatus !== 1) {
          throw new Error('Username or password is not correct, please try again');
        }

        const loginRet: LoginRet = {
          retCode: 0,
          twofEmails: [],
          twofPhones: [],
        };

        if (response.twof) {
          if (response.twofAskMethod) {
            // Two-factor authentication required; user needs to choose method
            loginRet.retCode = 2;
            loginRet.twofPhones = response.twofPhones || [];
            loginRet.twofEmails = response.twofEmails || [];
            return loginRet;
          } else {
            // Two-factor authentication with a token required
            loginRet.retCode = 3;
            return loginRet;
          }
        }

        // Successful login
        this.generalService.goToLandingPage = true;
        loginRet.retCode = 1;

        // Optionally store the user data locally
        localStorage.setItem('user', JSON.stringify(response));

        return loginRet;
      }),
      catchError((error) => {
        console.error('Login error:', error);
        return throwError(() => new Error(error.error || 'Login failed'));
      })
    );
  }

  // Fetch Salesforce Configuration (Client ID, Redirect URI, Auth URL)
  getSalesforceConfig(): Observable<{ clientId: string, redirectUri: string, authUrl: string }> {
    return this.http.get<{ clientId: string, redirectUri: string, authUrl: string }>(`${this.baseUrl}/getsalesforceconfig`);
  }

  // Call salesforce-login API before redirecting
  initiateSalesforceLogin(): Observable<{ loginUrl: string }> {
    return this.http.get<{ loginUrl: string }>(`${this.baseUrl}/salesforce-login`, { withCredentials: true });
  }

  // Authenticate Salesforce User After OAuth Redirect
  authenticateSalesforceUser(code: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/salesforce-authenticate`, { code });
  }


  // Change Password method
  changePassword(model: IChangePasswordModel): Observable<any> {
    return this.http.post(`${this.baseUrl}/changepassword`, model).pipe(
      catchError(this.handleError)
    );
  }

  // Forgot Password method
  //forgotPassword(model: IForgotPasswordModel): Observable<any> {
  //  return this.http.post(`${this.baseUrl}/forgotpassword`, model).pipe(
  //    catchError(this.handleError)
  //  );
  //}

  forgotPassword(model: IForgotPasswordModel): Observable<any> {
    return this.http.post(`${this.baseUrl}/forgotpassword`, model, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    }).pipe(catchError(this.handleError));
  }


  // Reset Password method
  resetPassword(model: IResetPasswordModel): Observable<any> {
    return this.http.post(`${this.baseUrl}/resetpassword`, model, {
      responseType: 'text' // ✅ Accepts text instead of JSON
    }).pipe(
      tap(response => console.log("Raw API Response:", response)), // ✅ Debugging response
      catchError(this.handleError)
    );
  }

  // Setup Account method
  setupAccount(model: ISetupAccountModel): Observable<any> {
    return this.http.post(`${this.baseUrl}/setupaccount`, model).pipe(
      catchError(this.handleError)
    );
  }

  // Get User method
  getUser(): Observable<IUser> {
    return this.http.get<IUser>(`${this.baseUrl}/getuser`).pipe(
      map((response) => {
        localStorage.setItem('user', JSON.stringify(response));
        return response;
      }),
      catchError(this.handleError)
    );
  }

  // Logout method
  logout(): void {
    localStorage.clear();
    this.http.post(`${this.baseUrl}/logout`, {}).subscribe({
      next: () => { window.location.href = '/identity'; },
      error: () => { window.location.href = '/identity'; },
    });
  }

  // Error handling helper
  private handleError(error: HttpErrorResponse): Observable<never> {
    console.error('An error occurred:', error);
    return throwError(() => new Error(error.message || 'Server error'));
  }

  getUserLocal(): Observable<IUser> {
    // Attempt to load user data from localStorage first
    const cachedUser = localStorage.getItem('user');
    if (cachedUser) {
      return of(JSON.parse(cachedUser) as IUser); // Emit cached user data
    }

    // Fetch user data from the backend
    return this.http.get<IUser>(`${this.baseUrl}/getuser`).pipe(
      tap((user: IUser) => {
        // Cache the user data in localStorage
        localStorage.setItem('user', JSON.stringify(user));
      }),
      catchError((err) => {
        // Handle errors, potentially logout the user
        this.logout();
        console.error(`Failed to fetch user data: ${err.message}`);
        return throwError(() => err);
      })
    );
  }

  endImpersonation(): Observable<{}> {
    return this.http.post<{}>(`${this.baseUrl}/endimpersonation/`, null, {
      observe: 'response',
      responseType: 'text' as 'json'
    });
  }

  /**
 * Impersonates a user by their ID.
 * @param userId The ID of the user to impersonate.
 * @returns An Observable of the HTTP response.
 */
  impersonateUser(userId: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/impersonate/${userId}`,
      { userId }, { observe: 'response', responseType: 'text' as 'json' });
  }
}
